/**
 * VisualFrame — superficie de edicao do modo "Visual" do TemplateEditor,
 * dentro de um iframe com body contentEditable.
 *
 * Por que iframe e nao div contenteditable: o valor pode carregar um bloco
 * <style> (fragmento do modo Blocos) ou um documento completo com CSS no
 * <head>. Num div, esse CSS vira stylesheet vivo do ERP inteiro — foi o bug
 * que deixou o app ilegivel em dark mode com template aberto no Visual. No
 * iframe o CSS fica contido e o Visual renderiza IGUAL a pre-visualizacao e
 * a pagina publica (HtmlTemplateFrame).
 *
 * Sem sandbox — a edicao (execCommand/eventos) precisa do documento vivo,
 * como o canvas do GrapesJS. A defesa e a sanitizacao: o corpo passa por
 * DOMPurify (remove script/on*) antes de entrar no iframe.
 *
 * Exportacao preserva a FORMA original do valor (fragmento ou documento
 * completo) via parse/serializeTemplateHtml, e so emite onChange quando o
 * conteudo realmente mudou — focar e sair do editor nao reescreve o campo.
 */
import DOMPurify from "dompurify";
import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
} from "react";

import { parseTemplateHtml, serializeTemplateHtml } from "./templateHtml";

// Base neutra (aplicada ANTES do CSS do autor, que vence conflitos).
// padding 0: a pagina publica renderiza full-bleed — padding aqui criaria
// uma calha que o cliente nao ve e o Visual deixaria de ser fiel.
const CSS_EDITOR =
  "html,body{min-height:100%}" +
  "body{font-family:Arial,Helvetica,sans-serif;color:#1a1a1a;margin:0;" +
  "padding:0;line-height:1.6;box-sizing:border-box;outline:none;" +
  "background:#fff}" +
  "table{border-collapse:collapse}img{max-width:100%}";

const VisualFrame = forwardRef(function VisualFrame({ value, onChange }, ref) {
  const frameRef = useRef(null);
  const formaRef = useRef(null);
  // Ultimo valor conhecido do campo — baseline para emitir so mudanca real.
  const baselineRef = useRef(value || "");

  // O modo Visual desmonta ao trocar de aba, entao o srcDoc e montado uma
  // vez por abertura com o valor corrente — sem re-render durante digitacao
  // (que destruiria o cursor).
  const srcDoc = useMemo(() => {
    const forma = parseTemplateHtml(value || "");
    formaRef.current = forma;
    baselineRef.current = value || "";
    const corpo = DOMPurify.sanitize(forma.bodyHtml, {
      USE_PROFILES: { html: true },
    });
    const styleAutor = forma.css ? `<style>${forma.css}</style>` : "";
    return (
      '<!doctype html><html><head><meta charset="utf-8">' +
      `${forma.headLinks}<style>${CSS_EDITOR}</style>${styleAutor}</head>` +
      `<body>${corpo}</body></html>`
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const docEditavel = useCallback(() => {
    try {
      return frameRef.current?.contentDocument || null;
    } catch {
      return null;
    }
  }, []);

  const syncOut = useCallback(() => {
    const doc = docEditavel();
    if (!doc?.body || !formaRef.current) return;
    const serializado = serializeTemplateHtml({
      ...formaRef.current,
      bodyHtml: doc.body.innerHTML,
    });
    if (serializado === baselineRef.current) return;
    baselineRef.current = serializado;
    onChange?.(serializado);
  }, [docEditavel, onChange]);

  const aoCarregar = useCallback(() => {
    const doc = docEditavel();
    if (!doc?.body || !formaRef.current) return;
    doc.body.contentEditable = "true";
    // Baseline re-serializada do DOM: diferencas de normalizacao do parser
    // (aspas, ordem de atributos) nao contam como edicao — focar e sair do
    // editor sem digitar nada nao pode reescrever o campo.
    baselineRef.current = serializeTemplateHtml({
      ...formaRef.current,
      bodyHtml: doc.body.innerHTML,
    });
    doc.body.addEventListener("input", syncOut);
    doc.body.addEventListener("blur", syncOut);
  }, [docEditavel, syncOut]);

  useImperativeHandle(
    ref,
    () => ({
      exec(command, arg) {
        const doc = docEditavel();
        if (!doc) return;
        doc.body.focus();
        doc.execCommand(command, false, arg);
        syncOut();
      },
      insertText(texto) {
        const doc = docEditavel();
        if (!doc) return;
        doc.body.focus();
        doc.execCommand("insertText", false, texto);
        syncOut();
      },
    }),
    [docEditavel, syncOut]
  );

  return (
    <iframe
      ref={frameRef}
      title="Edição visual do template"
      srcDoc={srcDoc}
      onLoad={aoCarregar}
      style={{
        flex: 1,
        minHeight: 0,
        width: "100%",
        border: 0,
        background: "#fff",
        display: "block",
      }}
    />
  );
});

export default VisualFrame;
